import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  variant?: 'center' | 'side';
  className?: string;
};

export default function PropertyModal({
  open,
  onClose,
  children,
  variant = 'center',
  className,
}: Props) {
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !contentRef.current) return;
    const el = contentRef.current;

    const stopPropagation = (event: Event) => {
      event.stopPropagation();
    };

    el.addEventListener('touchstart', stopPropagation, { passive: true });
    el.addEventListener('touchmove', stopPropagation, { passive: false });
    el.addEventListener('wheel', stopPropagation, { passive: false });

    return () => {
      el.removeEventListener('touchstart', stopPropagation);
      el.removeEventListener('touchmove', stopPropagation);
      el.removeEventListener('wheel', stopPropagation);
    };
  }, [open]);

  if (!open) return null;

  const baseContentClasses = clsx(
    'relative max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl',
    'overscroll-contain ios-touch-fix touch-pan-y',
    variant === 'center' && 'w-[94vw] md:w-[90vw] lg:w-[85vw] xl:max-w-6xl',
    variant === 'side' && 'w-[92vw] max-w-[480px] sm:w-[420px] sm:rounded-none sm:h-full',
  );

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      className={clsx(
        'fixed inset-0 z-[9999] bg-black/55 backdrop-blur-[1px]',
        variant === 'side' ? 'flex justify-end items-stretch' : 'flex items-center justify-center',
      )}
      onClick={onClose}
    >
      <div
        ref={contentRef}
        className={clsx(baseContentClasses, className)}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
